import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import styled from '@emotion/styled'
import { DropdownMenu } from 'components/patterns'
import { themeColor } from 'utils/styles'
import ProductFeatureGroup from 'components/patterns/ProductFeatureGroup'
import { useSelector } from 'hooks/redux'
import { getAddToSearchRequestBody } from 'selectors/search'
import { VALID_STATUSES, FEATURE } from 'utils/constants'

export function FeatureRevisionSelector({
  onSelect,
  navigateOnSelect = true,
  revision: revisionProp = '',
  feature: { revision, revisions = [], id: featureId } = {},
  collectionName = FEATURE,
  filterConflicts: filterConflictsProp,
}) {
  const history = useHistory()
  const addToSearchRequestBody = useSelector(
    getAddToSearchRequestBody,
    collectionName
  )
  // If caller explicitly provided filterConflicts prop, use it directly
  // Otherwise fall back to the selector-based value from addToSearchRequestBody
  // Handle both Immutable.js Map (uses .get()) and plain JS object
  const filterConflicts =
    filterConflictsProp !== undefined
      ? filterConflictsProp
      : addToSearchRequestBody?.get?.('filterConflicts') ??
        addToSearchRequestBody?.filterConflicts ??
        true

  const handleSelectedRevision = (revisionData) => {
    // Call onSelect callback if provided
    if (onSelect) {
      onSelect(revisionData)
    }

    // Navigate if requested (default behavior for backward compatibility)
    if (navigateOnSelect && revisionData.uri) {
      history.push(revisionData.uri)
    }
  }
  // Filter revisions by valid status when "Only Show Compatible Features" is checked
  // Use case-insensitive comparison since API may return different casing (e.g., 'ACTIVE' vs 'Active')
  const filteredRevisions = filterConflicts
    ? revisions.filter((rev) =>
        VALID_STATUSES.some(
          (status) => status.toLowerCase() === rev.status?.toLowerCase()
        )
      )
    : revisions

  // Track if we've already auto-selected to prevent infinite loops
  const hasAutoSelected = useRef(false)

  // Auto-select the single valid revision when filtering reduces to exactly one option
  // and it differs from the current feature revision
  useEffect(() => {
    if (
      filterConflicts &&
      filteredRevisions.length === 1 &&
      onSelect &&
      !hasAutoSelected.current
    ) {
      const singleValidRevision = filteredRevisions[0]
      const currentRevisionValue = revision
      const filteredRevisionValue =
        singleValidRevision.revision || singleValidRevision.release

      // Auto-select if the filtered revision differs from current
      if (filteredRevisionValue !== currentRevisionValue) {
        hasAutoSelected.current = true
        onSelect(singleValidRevision)
      }
    }

    // Reset the flag when filter is turned off or revisions change
    if (!filterConflicts || filteredRevisions.length !== 1) {
      hasAutoSelected.current = false
    }
  }, [filterConflicts, filteredRevisions, revision, onSelect])

  // Sort revisions in descending order (highest first) before mapping to dropdown options
  const sortedRevisions = filteredRevisions.slice().sort((a, b) => {
    const revisionA = a.revision || a.release || ''
    const revisionB = b.revision || b.release || ''

    // Parse as floats to handle numeric comparison (e.g., "1.0", "2.0", "10.0")
    const numA = parseFloat(revisionA)
    const numB = parseFloat(revisionB)

    // If both parse successfully as numbers, compare numerically
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
      return numB - numA // Descending order
    }

    // Fallback to string comparison for non-numeric values
    return revisionB.localeCompare(revisionA, undefined, { numeric: true })
  })

  const options = sortedRevisions.map((revisionObj) => ({
    key: `revision-${revisionObj.uri || revisionObj.id}`,
    onClick: () => handleSelectedRevision(revisionObj),
    display: revisionObj.revision || revisionObj.release,
  }))

  // Show plain text if only one revision available (after filtering)
  if (sortedRevisions.length <= 1) {
    // Use the filtered revision's value if available, otherwise fall back to the feature's current revision
    const displayRevision =
      sortedRevisions[0]?.revision || sortedRevisions[0]?.release || revision
    return <RevisionText>{displayRevision}</RevisionText>
  }

  return (
    <ProductFeatureGroup name="revise-active-feature" featureId={featureId}>
      <StyleLayout>
        <DropdownMenu
          options={options}
          text={revision}
          name={`revision-selector-${featureId}`}
          trigger="click"
          size="large"
          iconFill="slateSecondary"
          className="revision-selector"
        />
      </StyleLayout>
    </ProductFeatureGroup>
  )
}

FeatureRevisionSelector.propTypes = {
  featureId: PropTypes.string,
  onSelect: PropTypes.func,
  navigateOnSelect: PropTypes.bool,
  feature: PropTypes.shape({
    revision: PropTypes.string,
    revisions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        revision: PropTypes.string,
        uri: PropTypes.string,
        status: PropTypes.string,
      })
    ),
  }),
  revision: PropTypes.string,
  collectionName: PropTypes.string,
  filterConflicts: PropTypes.bool,
}

const RevisionText = styled.span`
  color: ${themeColor('blackSecondary')};
  font-family: Arial;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
`

const StyleLayout = styled.div`
  & .revision-selector .ant-btn {
    border: 0px;
    color: ${themeColor('slateSecondary')};
    font-family: Arial;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    background: transparent;
  }
  & .revision-selector button.ant-btn.chec-button {
    border: 0px;
    color: ${themeColor('blackSecondary')};
    font-family: Arial;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    margin: 0px;
    margin-top: -30px;
    margin-bottom: -30px;
    padding: 0px;
    background: transparent;
  }
`
